<template>
  <div class="bg-light">
    <header class="main-header">
      <div class="container">
        <div class="d-flex h-110">
          <div class="logo">
            <router-link :to="toLogin">
              <img src="@/assets/img/logo.svg" alt="" />
            </router-link>
          </div>
          <div class="header-title align-self-center">
            <img src="@/assets/img/21grams-dark-logo.svg" alt="" />
          </div>
        </div>
      </div>
    </header>
    <div class="page-body">
      <template v-if="!created">
        <h5 class="form-title title center-text">
          Interested in ParcelPortal?
          <br />
          Fill the form and we will contact you.
        </h5>
        <div class="container">
          <div class="wrap-form">
            <div>
              <VeeForm @submit="createCustomer">
                <Field v-slot="{ field, errors }" name="email" :rules="{ email: true, required: true, min: 3 }">
                  <b-form-group class="form-group position-relative">
                    <input
                      v-bind="field"
                      v-model="customer.email"
                      type="text"
                      name="Email address *"
                      class="form-control"
                      placeholder="Email address *"
                    />
                    <div class="invalid-feedback">
                      {{ errors.length ? errors.join() : '' }}
                    </div>
                  </b-form-group>
                </Field>
                <Field v-slot="{ field, errors }" name="firstName" :rules="{ required: true, min: 3 }">
                  <b-form-group class="form-group position-relative">
                    <input
                      v-bind="field"
                      v-model="customer.firstName"
                      type="text"
                      name="First name *"
                      class="form-control"
                      placeholder="First name *"
                    />
                    <div class="invalid-feedback">
                      {{ errors.length ? errors.join() : '' }}
                    </div>
                  </b-form-group>
                </Field>
                <Field v-slot="{ field, errors }" name="lastName" :rules="{ required: true, min: 3 }">
                  <b-form-group class="form-group position-relative">
                    <input
                      v-bind="field"
                      v-model="customer.lastName"
                      type="text"
                      name="Last name *"
                      class="form-control"
                      placeholder="Last name *"
                    />
                    <div class="invalid-feedback">
                      {{ errors.length ? errors.join() : '' }}
                    </div>
                  </b-form-group>
                </Field>
                <Field v-slot="{ field, errors }" name="company" rules="required">
                  <b-form-group class="form-group position-relative">
                    <input
                      v-bind="field"
                      v-model="customer.company"
                      type="text"
                      name="Company *"
                      class="form-control"
                      placeholder="Company *"
                    />
                    <div class="invalid-feedback">
                      {{ errors.length ? errors.join() : '' }}
                    </div>
                  </b-form-group>
                </Field>
                <Field v-slot="{ field, errors }" name="phoneNumber" :rules="{ phone: true, required: true }">
                  <b-form-group class="form-group position-relative">
                    <input
                      v-bind="field"
                      v-model="customer.phoneNumber"
                      type="text"
                      name="Phone number*"
                      class="form-control"
                      placeholder="Phone number*"
                    />
                    <div class="invalid-feedback">
                      {{ errors.length ? errors.join() : '' }}
                    </div>
                  </b-form-group>
                </Field>
                <b-alert :show="serverErrors" variant="danger">
                  Customer not created, please check fields errors or contact administrator
                </b-alert>
                <b-button type="submit" class="w-100"> Request Access </b-button>
              </VeeForm>
              <router-link :to="toLogin" class="color-grey"> Already have an account? Log in </router-link>
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="main-full-height">
          <div class="container">
            <div class="wrap-form center-text">
              <h5 class="center-text mb-4">Thanks for registering your interest! We will contact you soon.</h5>
              <b-button class="assignmentButton mt-2" type="submit" :to="toLogin"> Log In </b-button>
            </div>
          </div>
        </div>
      </template>
    </div>
    <LayoutFooter />
  </div>
</template>

<script lang="ts">
import type { RouterLink } from '@/types/Components/Router'
import type { ErrorsAccessor } from '@/types/Helpers/Arrays'
import type { RegistrationData } from '@/types/Models/Account'
import { useNotification } from '@kyvg/vue3-notification'
import { Form as VeeForm, Field } from 'vee-validate'
import { ref, defineComponent, type Ref } from 'vue'
import { removeEmpty } from '@/services/Helpers'
import { useCustomersStore } from '@/store/customers'
import { LOGIN_PAGE } from '@/types/router'
import LayoutFooter from '@/views/Layouts/LayoutFooter.vue'

export default defineComponent({
  name: 'CustomerRegistration',
  components: {
    VeeForm,
    Field,
    LayoutFooter,
  },
  setup() {
    const customerStore = useCustomersStore()
    const { notify } = useNotification()

    const created: Ref<boolean> = ref(false)
    const serverErrors: Ref<boolean> = ref(false)
    const customer: Ref<RegistrationData> = ref({
      email: '',
      firstName: '',
      lastName: '',
      company: '',
      phoneNumber: '',
    })

    const toLogin: RouterLink = {
      name: LOGIN_PAGE,
    }

    const createCustomer = (values: RegistrationData | any) => {
      const data = removeEmpty(values) as RegistrationData
      customerStore
        .requestCreate(data)
        .then(() => {
          created.value = true
        })
        .catch((response: ErrorsAccessor) => {
          serverErrors.value = true
          Object.values(response.errors).forEach((errorGroup: string[]) => {
            errorGroup.forEach((error: string) => {
              notify({ type: 'error', text: error })
            })
          })
        })
    }

    return {
      customer,
      created,
      toLogin,
      serverErrors,
      createCustomer,
    }
  },
})
</script>

<style lang="scss" scoped>
.client-city {
  width: 390px;
  margin-right: 16px;
}

.form-title {
  width: 600px;
  margin: 80px auto 0 auto;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 30px;
  letter-spacing: 0;
  text-align: center;
}

.assignmentButton {
  width: 365px;
}

:deep(.form-control) {
  background: #ffffff;
  border: 1px solid #cccccc;
  box-sizing: border-box;
  border-radius: 4px;
  color: #757575;
}

:deep(.custom-select) {
  border: 1px solid #cccccc;
  padding-left: 25px;
  color: #757575;
}

.color-grey {
  display: inline-block;
  font-style: normal;
  font-weight: 500;
  font-size: 13px;
  line-height: 150%;
  color: #757575;
  margin-top: 12px;
}
.bg-light {
  min-height: 100%;
  display: flex;
  flex-direction: column;
}
footer {
  margin-top: auto;
}
</style>
